import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import { blogArticles } from '@/mocks/blogArticles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.cam';

export default function BlogPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              Back to Homepage
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet Editorial Team',
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/blog/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet',
      url: siteUrl,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />

        {/* Article Header */}
        <section className="pt-28 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-400">Blog</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-400 truncate max-w-[200px]">{article.title}</span>
            </div>
            <p className="text-sm font-semibold text-primary-500 tracking-widest uppercase mb-4">
              {article.category}
            </p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-6 text-sm text-foreground-400">
              <span>By PandabuySpreadsheet Editorial Team</span>
              <span>&middot;</span>
              <span>{new Date(article.datePublished).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 -mt-6">
          <div className="rounded-2xl overflow-hidden aspect-[21/9] bg-background-100">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </section>

        {/* Article Body */}
        <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div
            className="prose prose-lg max-w-none space-y-5 text-foreground-700 leading-relaxed text-base md:text-lg"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* FAQ Section */}
          <div className="mt-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-5">
              {article.faqs.map((faq, idx) => (
                <div key={idx}>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-foreground-700 leading-relaxed text-base">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="bg-primary-500/10 rounded-xl p-6 md:p-8 mt-10 text-center">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
              Start Comparing Smarter Today
            </h3>
            <p className="text-foreground-600 text-sm md:text-base mb-5 max-w-md mx-auto">
              Access structured product comparison data across 15 categories on the Pandabuy Sheet platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Explore Pandabuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-foreground-200 text-foreground-700 font-semibold text-sm hover:border-foreground-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </article>

        <FooterSection />
      </main>
    </>
  );
}